import { useEffect } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Layout from '@components/organisms/Layouts/WithoutSidebar'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import { getLiveCourse } from '@services/api/course'
import styled from '@emotion/styled'
import { Button, CircularProgress } from '@nextui-org/react-latest'
import { LiveCourse } from '@type/course'
import Version from '@type/course/version'
import { convertKeywordsToString } from '@utils/convertKeywordsToString'
import useAnalytics from '@hooks/useAnalytics'
import { useCartAndWishList } from '@contexts/CartAndWishList'
import useReferralTracking from '@hooks/useReferralTracking'
import { GetServerSidePropsContext } from 'next'
import { currentDomain } from '@configs/env'
import { CourseNotFoundIcon } from '@public/static/icons/course-not-found-icon'
import { logEvent } from 'firebase/analytics'
import { analytics } from '@configs/firebase'

const MobileHeader = dynamic(
  () => import('@components/pages/course/MobileHeader'),
  {
    loading: () => <div className="h-[70px]" />,
  }
)

const BottomMargin = styled.div`
  @media (max-width: 1049px) {
    height: 100px;
  }
  @media (max-width: 1000px) {
    height: 88px;
  }
`

const CoursePageBlock = dynamic(
  () => import('components/organisms/CoursePageBlock'),
  {
    loading: () => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          paddingTop: '29vh',
        }}
      >
        <CircularProgress />
      </div>
    ),
  }
)

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const { locale = 'en', query, res } = ctx
  const { course_id } = query

  let course: LiveCourse | null
  try {
    if (course_id) {
      course = await getLiveCourse(course_id)
    } else {
      course = null
    }
  } catch (error) {
    course = null
  }

  res.setHeader(
    'Cache-Control',
    'no-store'
  )

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'authentication',
        'footer',
        'courses',
        'reportProblem',
        'stickyCoursePageBlock',
        'aboutAuthor',
        'courseReviews',
        'home',
        'videoPlayer',
        'giftCard',
        'wishlist',
        'wishlistDropdown',
        'cartModal',
        'review',
        'cart',
      ])),
      course,
    },
  }
}

const Course = ({ course }: { course: Version | null }) => {
  const router = useRouter()
  const { course_id } = router.query
  const { isAnalyticsReady, captureEvent, events } = useAnalytics()
  const { t } = useTranslation(['common', 'courses', 'footer'])
  const {
    checkDiscount,
    storeReferralCodeInLocalStorage,
    cartItems,
    addDiscounts,
    discounts,
    getReferralCodeFromLocalStorage,
  } = useCartAndWishList()
  useReferralTracking()

  useEffect(() => {
    if (course_id && isAnalyticsReady) {
      captureEvent(events?.PAGE_VIEW as never, course_id, 1)
    }
    if (course_id && course && analytics) {
      logEvent(analytics, 'view_item', {
        currency: 'USD',
        value: (course.salePrice ?? course.price ?? 0) / 100,
        items: [
          {
            item_id: course._id,
            item_name: course.title,
            item_category:
              course.category?.name?.['en'] ?? course.category?.name?.['uk'],
            price: (course.salePrice ?? course.price ?? 0) / 100,
          },
        ],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_id, isAnalyticsReady])

  const handleCode = async (code: string) => {
    try {
      const res = await checkDiscount(code)
      if (res) {
        const existingCode = getReferralCodeFromLocalStorage()
        if (existingCode) {
          if (existingCode !== code) storeReferralCodeInLocalStorage(code)
        } else {
          storeReferralCodeInLocalStorage(code)
        }
        if (cartItems.length > 0) {
          if (discounts.find((d) => d?.referralCode === code)) return
          await addDiscounts(code)
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {}
  }

  useEffect(() => {
    if (router.query.code) {
      handleCode(String(router.query.code))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router])

  return (
    <>
      <Head>
        <title>{course?.title}</title>
        <meta property="og:title" content={course?.title} />
        <meta name="title" content={course?.title} />
        <meta
          property="og:description"
          content={course?.shortCourseDescription}
        />
        <meta name="description" content={course?.shortCourseDescription} />
        <meta property="og:image" content={course?.presentationalImage} />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="720" />
        <meta
          name="keywords"
          content={convertKeywordsToString(course?.keywords)}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={course?.title} />
        <meta
          name="twitter:description"
          content={course?.shortCourseDescription}
        />
        <meta name="twitter:image" content={course?.presentationalImage} />

        <meta
          property="og:video:url"
          content={course?.presentationalVideo?.public?.url}
        />
        {/* <meta
          property="og:video:secure_url"
          content={course?.presentationalVideo?.public?.url}
        />
        <meta property="og:video:type" content="video/mp4" />
        <meta property="og:video:width" content="1280" />
        <meta property="og:video:height" content="720" /> */}
        <link
          rel="canonical"
          href={`https://www.edugram.io/courses/${course_id}/`}
        />
      </Head>
      <Layout
        fullWidth={false}
        hideHeader={true}
        hideSearchOnMobile={true}
        customHeader={
          (<MobileHeader course={course} />) as unknown as
            | boolean
            | Element
            | undefined
        }
      >
        {course ? (
          <>
            <CoursePageBlock
              course={course as unknown as Version}
              course_id={course._id || (course_id as string)}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 px-4">
            <CourseNotFoundIcon />
            <h2 className="text-2xl md:text-3xl font-semiBold text-gray-800 mb-3">
              {t('messages.Course not found', { ns: 'courses' })}
            </h2>
            <p className="text-gray-500 text-center mb-8 max-w-sm">
              {t(
                'messages.The course you are looking for does not exist or has been removed',
                {
                  ns: 'courses',
                  defaultValue:
                    'The course you are looking for does not exist or has been removed.',
                }
              )}
            </p>
            <Button
              onPress={() => router.push('/courses')}
              color="primary"
              className="font-semiBold px-8"
              size="lg"
            >
              {t('messages.Browse Courses', {
                ns: 'courses',
                defaultValue: 'Browse Courses',
              })}
            </Button>
          </div>
        )}
      </Layout>
      <BottomMargin />
      {course && (
        <script
          id={course._id}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: course.title,
              image: course.presentationalImage,
              description: course.shortCourseDescription,
              sku: `${course._id}`,
              brand: {
                '@type': 'Brand',
                name: 'Edugram.io',
              },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: course?.rating,
                reviewCount: course?.ratingQty,
              },
              offers: {
                '@type': 'Offer',
                url: `${currentDomain}/course/${course?.courseNickname ? course.courseNickname : course?._id}`,
                priceCurrency: 'USD',
                price: course.price
                  ? course?.salePrice
                    ? `${course?.salePrice / 100}`
                    : `${course?.price / 100}`
                  : '0.00',
                availability: 'https://schema.org/InStock',
              },
            }),
          }}
        />
      )}
    </>
  )
}
export default Course
